import { DeleteOutline, EditOutlined, MapOutlined } from "@mui/icons-material";
import { Box, Button, IconButton, Popover, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useState } from "react";
import { getWorkedStatusConfig } from "../../../config/workstatus.config";
import { TimesheetRow } from "../../../interfaces/time-sheet.interfaces";
import { formatDuration } from "../helpers/formatduration.helpers";
import { getWorkedStatus } from "../helpers/getWorkedStatus.helpers";
import PunchLocationCollapse from "./PunchLocationCollapse";
import { StatusBadge } from "./StatusBadge";
import { BaseModalComponent } from "@components/common/BaseModalComponent";
import PunchLocationModal from "./PunchLocationModal";
import { useCompanyTimeZone } from "../../../../common/CompanyTime/hooks/useCompanyTimeZone";

const formatDate = (value?: string | null, timeZone?: string, format = "dd MMM yyyy hh:mm a") => {
  if (!value) return "-";

  const dt = DateTime.fromISO(value, { setZone: true }).setZone(timeZone || "utc");

  return dt.isValid ? dt.toFormat(format) : "-";
};

const formatTravel = (seconds?: number | null) => {
  if (seconds == null) return "—";
  return formatDuration(seconds);
};

const getWorkerName = (row: TimesheetRow) => {
  const name = `${row.workerFirstName ?? ""} ${row.workerLastName ?? ""}`.trim();

  return name || "No worker assigned";
};

const toCoordinate = (value?: number | string | null): number | null => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
};

interface Props {
  row: TimesheetRow;
  isGlobal: boolean;
  onEdit: (uid: string) => void;
  onDelete: (uid: string) => void;
  totalSecondsGrouped?: number;
  travelSecondsGrouped?: number | null;
  onSelectWorker?: (worker: any) => void;
}

export default function TimesheetTableRow({ onSelectWorker, row, isGlobal, totalSecondsGrouped, travelSecondsGrouped, onEdit, onDelete }: Props) {
  const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { timeZone } = useCompanyTimeZone();

  const scheduledRange = `${formatDate(row.startDate, timeZone, "hh:mm a")} - ${formatDate(row.endDate, timeZone, "hh:mm a")}`;

  const { totalSeconds: rowSeconds } = getWorkedStatus(row);

  const displaySeconds = isGlobal ? totalSecondsGrouped : rowSeconds;

  const { workedStatus, totalSeconds } = getWorkedStatus({
    startAt: row.startAt,
    endAt: row.endAt,
    startDate: row.startDate,
    endDate: row.endDate,
  });

  const startLatitude = toCoordinate(row.startLatitude);

  const startLongitude = toCoordinate(row.startLongitude);

  const endLatitude = toCoordinate(row.endLatitude);

  const endLongitude = toCoordinate(row.endLongitude);

  const hasPunchInLocation = startLatitude !== null && startLongitude !== null;

  const hasPunchOutLocation = endLatitude !== null && endLongitude !== null;

  const hasAnyLocation = hasPunchInLocation || hasPunchOutLocation;

  const hasPunchIn = Boolean(row.startAt);
  const hasPunchOut = Boolean(row.endAt);

  const hasAnyPunch = hasPunchIn || hasPunchOut;

  const locationTooltip = hasAnyLocation
    ? "View punch locations"
    : hasAnyPunch
      ? "Location was not enabled for this punch"
      : "No punches recorded yet";

  if (isGlobal) {
    return (
      <TableRow
        onClick={() =>
          onSelectWorker?.({
            uid: row.workforceUid,
            alias: `${row.workerFirstName} ${row.workerLastName}`,
            firstName: row.workerFirstName,
            lastName: row.workerLastName,
          })
        }
        sx={{
          cursor: "pointer",

          "&:hover": {
            bgcolor: "var(--fx-surface-muted, #F1F5F9)",
          },

          "& td": {
            borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)",
            py: 1.5,
          },
        }}
      >
        <TableCell align="left">
          <Box
            sx={{
              width: "fit-content",
              bgcolor: "var(--fx-surface-muted, #F1F5F9)",
              px: 1.2,
              py: 0.75,
              borderRadius: 1.5,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 800,
                color: "var(--fx-surface-textsecondary, #64748B)",
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
            >
              {getWorkerName(row)}
            </Typography>
          </Box>
        </TableCell>

        <TableCell align="right">
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: 13,
              color: "var(--fx-surface-text, #0F172A)",
            }}
          >
            {formatDuration(displaySeconds || 0)}
          </Typography>
        </TableCell>

        <TableCell align="right">
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: 13,
              color: "var(--fx-surface-text, #0F172A)",
            }}
          >
            {formatTravel(travelSecondsGrouped)}
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <TableRow
        sx={{
          "& td": {
            borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)",
            py: 1,
            px: 0.5,
          },
        }}
      >
        <TableCell align="center">
          <Box
            sx={{
              minWidth: 80,
              bgcolor: "var(--fx-surface-muted, #F1F5F9)",
              p: 0.5,
              borderRadius: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 800,
                color: "var(--fx-surface-textsecondary, #64748B)",
                lineHeight: 1,
              }}
            >
              {row.jobAlias || "Task"}
            </Typography>
          </Box>
        </TableCell>

        <TableCell align="center">
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 10,
              color: "var(--fx-surface-text, #0F172A)",
            }}
          >
            {formatDate(row.startDate, timeZone, "MMM dd, yyyy")}
          </Typography>

          <Typography
            sx={{
              fontSize: 9,
              color: "var(--fx-surface-textsecondary, #64748B)",
            }}
          >
            {scheduledRange}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 10,
            }}
          >
            {formatDate(row.startAt, timeZone, "hh:mm a")}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 10,
            }}
          >
            {formatDate(row.endAt, timeZone, "hh:mm a")}
          </Typography>
        </TableCell>

        <TableCell align="center">
          {hasAnyLocation ? (
            <BaseModalComponent
              modalKey={`punch-location-${row.workforceEventUid}`}
              modalLabel="Location Verification"
              fitContentHeight={true}
              contentFitHeight={true}
              elementTrigger={
                <Tooltip title={locationTooltip} arrow>
                  <span>
                    <IconButton
                      size="small"
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 1.5,
                        color: "var(--fx-surface-textsecondary, #64748B)",

                        "&:hover": {
                          bgcolor: "var(--fx-colors-info-50, #EFF6FF)",
                          color: "#2563eb",
                        },
                      }}
                    >
                      <MapOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              }
            >
              <PunchLocationModal row={row} />
            </BaseModalComponent>
          ) : (
            <Tooltip title={locationTooltip} arrow>
              <span>
                <IconButton
                  size="small"
                  disabled
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: 1.5,
                    color: "#cbd5e1",
                  }}
                >
                  <MapOutlined sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </TableCell>

        <TableCell align="center">
          <StatusBadge status={workedStatus} />
        </TableCell>

        <TableCell align="center">
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 11,
            }}
          >
            {formatDuration(totalSeconds)}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 11,
            }}
          >
            {formatTravel(row.travelSeconds)}
          </Typography>
          {row.estimatedTravelSeconds != null ? (
            <Typography sx={{ fontSize: 9, color: "var(--fx-surface-texttertiary, #94A3B8)" }}>Est. {formatTravel(row.estimatedTravelSeconds)}</Typography>
          ) : null}
        </TableCell>

        <TableCell align="right">
          <Box display="flex" justifyContent="flex-end">
            <IconButton size="small" onClick={() => onEdit(row.uid)}>
              <EditOutlined
                sx={{
                  fontSize: 18,
                }}
              />
            </IconButton>

            <IconButton size="small" onClick={(e) => setDeleteAnchorEl(e.currentTarget)}>
              <DeleteOutline
                sx={{
                  fontSize: 18,
                }}
              />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>

      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={() => setDeleteAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            p: 2,
            width: 250,
            borderRadius: 3,
            border: "1px solid var(--fx-surface-border, #E2E8F0)",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 800,
            color: "var(--fx-surface-text, #0F172A)",
            mb: 0.5,
          }}
        >
          Delete entry?
        </Typography>

        <Typography
          sx={{
            fontSize: 11,
            color: "var(--fx-surface-textsecondary, #64748B)",
            mb: 2,
          }}
        >
          Are you sure you want to delete this record? This action cannot be undone.
        </Typography>

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button
            size="small"
            onClick={() => setDeleteAnchorEl(null)}
            sx={{
              fontSize: 10,
              fontWeight: 800,
              textTransform: "none",
              color: "var(--fx-surface-textsecondary, #64748B)",
              borderRadius: 1.5,
              py: 0.5,
              px: 1.5,
            }}
          >
            Cancel
          </Button>

          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={() => {
              onDelete(row.workforceEventUid);

              setDeleteAnchorEl(null);
            }}
            sx={{
              fontSize: 10,
              fontWeight: 800,
              textTransform: "none",
              borderRadius: 1.5,
              boxShadow: "none",
              py: 0.5,
              px: 1.5,

              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            Delete
          </Button>
        </Box>
      </Popover>
    </>
  );
}
